import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Repeat, Gift, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import ShareBar from "@/components/ShareBar";

export default function Donate() {
  useSeo("Donate", "Make a secure one-time or recurring monthly donation to Golden Wolf Foundation and support service dogs for veterans.");
  const [cfg, setCfg] = useState({ presets: [55, 155, 555, 1555, 5555], campaigns: [] });
  const [amount, setAmount] = useState(155);
  const [custom, setCustom] = useState("");
  const [recurring, setRecurring] = useState(false);
  const [campaign, setCampaign] = useState("Greatest Need");
  const [form, setForm] = useState({ donor_name: "", email: "" });
  const [consent, setConsent] = useState({ public_recognition: false, anonymous: false, tax_receipt_requested: true });
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/donations/config").then((r) => { setCfg(r.data); setCampaign(r.data.campaigns[r.data.campaigns.length - 1] || "Greatest Need"); }); }, []);

  const finalAmount = custom ? parseFloat(custom) : amount;

  const submit = async (e) => {
    e.preventDefault();
    if (!finalAmount || finalAmount < 5) return toast.error("Please enter an amount of at least $5");
    if (!form.email) return toast.error("Email is required for your receipt");
    setLoading(true);
    try {
      const res = await api.post("/donations/checkout", {
        amount: finalAmount, recurring, campaign, ...form, ...consent,
        membership_tier: recurring ? "The Golden Pack" : null,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-16">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="overline mb-3">Support the Mission</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#FDFBF7]">Make a Donation</h1>
        <p className="mt-4 text-lg text-[#C8C4B7] max-w-2xl">Your gift trains dogs, funds trainer scholarships, and rebuilds purpose for veterans. Give once, or join <span className="text-[#D4AF37]">The Golden Pack</span> with a monthly gift.</p>
      </motion.div>

      <div className="mt-10 grid lg:grid-cols-5 gap-8">
        <form onSubmit={submit} className="lg:col-span-3 space-y-8">
          {/* Frequency */}
          <div className="grid grid-cols-2 gap-3">
            <button type="button" onClick={() => setRecurring(false)} data-testid="freq-once"
              className={`flex items-center justify-center gap-2 py-4 border font-semibold transition-colors ${!recurring ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/20 text-[#C8C4B7] hover:border-[#D4AF37]"}`}>
              <Gift className="w-4 h-4" /> One-Time
            </button>
            <button type="button" onClick={() => setRecurring(true)} data-testid="freq-monthly"
              className={`flex items-center justify-center gap-2 py-4 border font-semibold transition-colors ${recurring ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/20 text-[#C8C4B7] hover:border-[#D4AF37]"}`}>
              <Repeat className="w-4 h-4" /> Monthly (Golden Pack)
            </button>
          </div>

          {/* Amounts */}
          <div>
            <div className="text-sm text-[#C8C4B7] mb-3">Choose an amount</div>
            <div className="grid grid-cols-3 gap-3">
              {cfg.presets.map((p) => (
                <button type="button" key={p} data-testid={`amount-${p}`}
                  onClick={() => { setAmount(p); setCustom(""); }}
                  className={`py-6 font-heading text-2xl font-bold border transition-colors ${!custom && amount === p ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "bg-[#141414] border-white/15 text-[#FDFBF7] hover:border-[#D4AF37]"}`}>
                  ${p.toLocaleString()}
                </button>
              ))}
              <div className={`flex items-center border transition-colors ${custom ? "border-[#D4AF37]" : "border-white/15"} bg-[#141414]`}>
                <span className="pl-4 text-[#8A877B] text-xl">$</span>
                <input type="number" min="5" placeholder="Custom" value={custom} data-testid="amount-custom"
                  onChange={(e) => setCustom(e.target.value)}
                  className="w-full bg-transparent px-2 py-6 text-xl text-[#FDFBF7] outline-none font-heading" />
              </div>
            </div>
          </div>

          {/* Campaign */}
          <div>
            <label className="block text-sm text-[#C8C4B7] mb-2">Designate my gift to</label>
            <select value={campaign} onChange={(e) => setCampaign(e.target.value)} className="gw-input" data-testid="donate-campaign">
              {cfg.campaigns.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {/* Donor */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-[#C8C4B7] mb-2">Full name</label>
              <input className="gw-input" data-testid="donate-name" value={form.donor_name} onChange={(e) => setForm({ ...form, donor_name: e.target.value })} placeholder="Jane Veteran" />
            </div>
            <div>
              <label className="block text-sm text-[#C8C4B7] mb-2">Email <span className="text-[#D4AF37]">*</span></label>
              <input type="email" required className="gw-input" data-testid="donate-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" />
            </div>
          </div>

          {/* Consent */}
          <div className="space-y-3">
            {[
              ["public_recognition", "List me publicly on the donor wall"],
              ["anonymous", "Keep my gift anonymous"],
              ["tax_receipt_requested", "Send me a tax acknowledgment receipt"],
            ].map(([k, label]) => (
              <label key={k} className="flex items-center gap-3 cursor-pointer text-sm text-[#C8C4B7]" data-testid={`consent-${k}`}>
                <span onClick={() => setConsent({ ...consent, [k]: !consent[k] })}
                  className={`w-5 h-5 border flex items-center justify-center transition-colors ${consent[k] ? "bg-[#D4AF37] border-[#D4AF37]" : "border-white/30"}`}>
                  {consent[k] && <Check className="w-3.5 h-3.5 text-black" />}
                </span>
                {label}
              </label>
            ))}
          </div>

          <button type="submit" disabled={loading} className="btn-gold w-full flex items-center justify-center gap-2 text-lg py-4" data-testid="donate-submit">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
            {recurring ? `Give $${(finalAmount || 0).toLocaleString()}/month` : `Donate $${(finalAmount || 0).toLocaleString()}`}
          </button>
          <p className="text-xs text-[#8A877B] text-center">Secure payment via Stripe. Test card: 4242 4242 4242 4242.</p>
        </form>

        {/* Sidebar */}
        <aside className="lg:col-span-2 space-y-6">
          <div className="gw-card p-7">
            <h3 className="font-heading text-xl font-semibold text-[#FDFBF7]">The Golden Pack</h3>
            <p className="mt-3 text-sm text-[#C8C4B7] leading-relaxed">Monthly members are the backbone of the mission — providing the steady support that trains a dog and transforms a veteran, month after month.</p>
            <ul className="mt-4 space-y-2 text-sm text-[#C8C4B7]">
              {["Founding Golden Pack member recognition", "Monthly impact updates", "Invitations to graduations & 14DDV"].map((b) => (
                <li key={b} className="flex gap-2"><Check className="w-4 h-4 text-[#D4AF37] shrink-0 mt-0.5" />{b}</li>
              ))}
            </ul>
          </div>
          <div className="gw-card p-7">
            <div className="overline mb-3">Where it goes</div>
            <ul className="space-y-2 text-sm text-[#C8C4B7]">
              {(cfg.campaigns || []).map((c) => <li key={c} className="flex gap-2"><span className="text-[#D4AF37]">—</span>{c}</li>)}
            </ul>
            <div className="mt-6"><ShareBar title="Support Golden Wolf Foundation" /></div>
          </div>
        </aside>
      </div>
    </div>
  );
}
